<template>
  <div v-if="member">
    <template v-if="useSpecialistProfileLayout">
      <v-container
        class="py-4"
        fluid
      >
        <router-link
          :to="backRoute"
          class="d-inline-flex align-center ga-2 c-black text-decoration-none mb-4"
          data-cy="back_to_staff_list"
        >
          <v-icon
            icon="arrow_back"
            size="20"
          />
          <span class="fs-16 fw-500">Back to Staff List</span>
        </router-link>

        <v-row>
          <v-col cols="12">
            <v-card
              border
              flat
              tile
            >
              <v-card-title class="pa-6 pb-4">
                <div>
                  <h1 class="fs-28 fw-600 c-black mb-1">
                    {{ profileName }}
                  </h1>
                  <div class="fs-16 c-gray">
                    {{ profileRole }}
                  </div>
                </div>
              </v-card-title>

              <v-divider />

              <StaffCard
                @change="handleStaffChange"
                :border="false"
                :member="member"
                :providers="providers"
                four-column-contact-layout
                four-column-employment-layout
                hide-employment-alert
                hide-locations-field
                hide-member-header
              />
            </v-card>
          </v-col>

          <v-col cols="12">
            <StaffDocumentsPanel
              v-if="showDocumentsPanel"
              @edit-expiry="openExpiryDialog"
              @review-status="openApproveDialog"
              @upload="openUploadDialog"
              :attachments="attachments"
              :can-review-documents="canReviewDocuments"
            />
          </v-col>
        </v-row>
      </v-container>
    </template>

    <template v-else>
      <PageTitle
        :back-name="backName"
        :back-route="backRoute"
        :title="pageTitle"
        outlined
        title-only
      />

      <v-container
        class="d-flex flex-column ga-4"
        fluid
      >
        <StaffCard
          @change="handleStaffChange"
          :member="member"
          :providers="providers"
        />

        <StaffDocumentsPanel
          v-if="showDocumentsPanel"
          @edit-expiry="openExpiryDialog"
          @review-status="openApproveDialog"
          @upload="openUploadDialog"
          :attachments="attachments"
          :can-review-documents="canReviewDocuments"
        />
      </v-container>
    </template>

    <StaffDocumentUploadDialog
      v-if="uploadDialogVisible"
      @close="uploadDialogVisible = false"
      @uploaded="handleAttachmentChange"
      ref="uploadDialogRef"
      :business-id="businessId"
      :categories="staffDocumentCategories"
      :employment-id="employmentId"
      :initial-category="uploadCategory"
    />

    <StaffDocumentApproveDialog
      v-if="approveDialogVisible"
      @approve="approveAttachment"
      @close="closeApproveDialog"
      @deny="denyAttachment"
      ref="approveDialogRef"
      :category-name="reviewRow?.category"
      :processing="dialogProcessing"
    />

    <StaffDocumentExpiryDialog
      v-if="expiryDialogVisible"
      @close="expiryDialogVisible = false"
      @save="saveExpiryDate"
      ref="expiryDialogRef"
      :initial-expires-at="expiryRow?.expiresAt"
    />
  </div>
</template>

<script setup>
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

import PageTitle from '@/shared/components/PageTitle.vue';
import { STAFF_DOCUMENT_CATEGORIES, formatExpiryNote } from '@/shared/utils/staffDocumentUtils';
import StaffCard from '@/shared/components/provider/StaffCard.vue';
import StaffDocumentApproveDialog from '@/shared/components/provider/staff-documents/StaffDocumentApproveDialog.vue';
import StaffDocumentExpiryDialog from '@/shared/components/provider/staff-documents/StaffDocumentExpiryDialog.vue';
import StaffDocumentsPanel from '@/shared/components/provider/staff-documents/StaffDocumentsPanel.vue';
import StaffDocumentUploadDialog from '@/shared/components/provider/staff-documents/StaffDocumentUploadDialog.vue';
import useApi from '@/shared/composables/useApi';
import useRole from '@/shared/composables/useRole';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { api, scopedApi } = useApi();
const { isManager, isSpecialist } = useRole();

const member = ref(null);
const providers = ref([]);
const attachments = ref([]);
const employmentId = ref(null);
const businessId = ref(null);

const uploadDialogVisible = ref(false);
const approveDialogVisible = ref(false);
const expiryDialogVisible = ref(false);
const uploadDialogRef = ref(null);
const approveDialogRef = ref(null);
const expiryDialogRef = ref(null);
const uploadCategory = ref(null);
const reviewRow = ref(null);
const expiryRow = ref(null);
const dialogProcessing = ref(false);

const staffDocumentCategories = STAFF_DOCUMENT_CATEGORIES;

const backName = 'Back to Staff List';

const useSpecialistProfileLayout = computed(() => isSpecialist && route.params.memberId !== 'new');

const backRoute = computed(() => {
  if (isSpecialist && route.params.providerId) {
    return {
      name: 'ProviderShow',
      params: { providerId: route.params.providerId },
      query: { tab: 'staff' },
    };
  }

  const query = route.query.providerId ? { providerId: route.query.providerId } : {};
  return { name: 'ProviderStaffIndex', query };
});

const pageTitle = computed(() => {
  if (!member.value?.id) return 'New staff member';

  const name = [member.value.first_name, member.value.last_name].filter(Boolean).join(' ');
  return name || member.value.name || 'Staff member';
});

const profileName = computed(() => {
  if (!member.value) return 'Staff member';

  const name = [member.value.first_name, member.value.last_name].filter(Boolean).join(' ');
  return name || member.value.name || 'Staff member';
});

const profileRole = computed(() => member.value?.title || 'Staff member');

const providerId = computed(() => route.params.providerId || route.query.providerId || null);

const canReviewDocuments = computed(() => isSpecialist);

const showDocumentsPanel = computed(
  () =>
    store.state.config?.has_published_licensing_programs &&
    (isSpecialist || isManager) &&
    employmentId.value &&
    route.params.memberId !== 'new',
);

watch(() => [route.params.memberId, route.params.providerId, route.query.providerId], load, {
  immediate: true,
});

async function loadProviders() {
  if (isSpecialist && providerId.value) {
    const providerResp = await scopedApi.provider.get(providerId.value);
    if (providerResp?.status !== 200) return;

    providers.value = [providerResp.data];
    businessId.value = providerResp.data.business_id;
    return;
  }

  const providerResp = await scopedApi.provider.index();
  if (providerResp?.status !== 200) return;

  providers.value = providerResp.data;

  if (providerId.value) {
    const selectedProvider = providerResp.data.find((provider) => provider.id === providerId.value);
    businessId.value = selectedProvider?.business_id || providerResp.data[0]?.business_id || null;
  } else {
    businessId.value = providerResp.data[0]?.business_id || null;
  }
}

function attachmentIndexApi() {
  return isSpecialist ? api.organization.attachment : api.manager.attachment;
}

function attachmentUpdateApi() {
  return isSpecialist ? api.organization.attachment : api.manager.attachment;
}

async function loadAttachments() {
  if (!employmentId.value) {
    attachments.value = [];
    return;
  }

  const response = await attachmentIndexApi().index({
    owner_id: employmentId.value,
    owner_type: 'Employment',
  });

  if (response?.status !== 200) return;

  attachments.value = response.data;
}

async function load() {
  await loadProviders();

  if (route.params.memberId === 'new') {
    employmentId.value = null;
    attachments.value = [];
    member.value = {
      employment: {
        start_date: new Date().toISOString().split('T')[0],
      },
      phone: '',
    };
    return;
  }

  const params = {
    provider_id: providerId.value || undefined,
    is_provider: true,
    include_deactivated: true,
  };
  const staffResp = await scopedApi.member.index(params);
  if (staffResp?.status !== 200) return;

  const memberData = staffResp.data.find((staffMember) => staffMember.id === route.params.memberId);
  if (!memberData) {
    await router.push(backRoute.value);
    return;
  }

  const resolvedBusinessId = businessId.value || providers.value[0]?.business_id;
  const employment =
    memberData.employments?.find(
      (record) => record.business_id === resolvedBusinessId && !record.end_date,
    ) ||
    memberData.employments?.find((record) => record.business_id === resolvedBusinessId) ||
    memberData.employments?.[0];

  employmentId.value = employment?.id || null;
  businessId.value = employment?.business_id || resolvedBusinessId || null;
  member.value = mergeMemberWithEmployment(memberData, employment);

  if (showDocumentsPanel.value) {
    await loadAttachments();
  }
}

function mergeMemberWithEmployment(memberData, employment) {
  return {
    ...memberData,
    ...(employment || {}),
    id: memberData.id,
    employment_record_id: employment?.id || null,
    phone: memberData.phone || '',
  };
}

async function handleStaffChange() {
  if (route.params.memberId === 'new') {
    await router.push(backRoute.value);
    return;
  }

  await load();
}

async function handleAttachmentChange() {
  uploadDialogVisible.value = false;
  await loadAttachments();
}

async function openUploadDialog() {
  uploadCategory.value = null;
  uploadDialogVisible.value = true;
  await nextTick();
  uploadDialogRef.value?.open();
}

async function openApproveDialog(row) {
  reviewRow.value = row;
  approveDialogVisible.value = true;
  await nextTick();
  approveDialogRef.value?.open();
}

function closeApproveDialog() {
  approveDialogVisible.value = false;
  reviewRow.value = null;
}

async function openExpiryDialog(row) {
  expiryRow.value = row;
  expiryDialogVisible.value = true;
  await nextTick();
  expiryDialogRef.value?.open(row.expiresAt);
}

async function approveAttachment() {
  if (!canReviewDocuments.value || !reviewRow.value?.attachment?.id) return;

  dialogProcessing.value = true;
  const response = await api.organization.attachment.update(reviewRow.value.attachment.id, {
    status: 'Accepted',
  });
  dialogProcessing.value = false;

  if (response?.status !== 200) return;

  closeApproveDialog();
  await loadAttachments();
}

async function denyAttachment() {
  if (!canReviewDocuments.value || !reviewRow.value?.attachment?.id) return;

  dialogProcessing.value = true;
  const response = await api.organization.attachment.update(reviewRow.value.attachment.id, {
    status: 'Rejected',
  });
  dialogProcessing.value = false;

  if (response?.status !== 200) return;

  closeApproveDialog();
  await loadAttachments();
}

async function saveExpiryDate(expiresAt) {
  const attachmentId = expiryRow.value?.attachment?.id;
  if (!attachmentId) return;

  const note = formatExpiryNote(expiresAt);
  if (!note) return;

  const response = await attachmentUpdateApi().update(attachmentId, { note });

  if (response?.status !== 200) return;

  expiryDialogRef.value?.close();
  expiryDialogVisible.value = false;
  expiryRow.value = null;
  await loadAttachments();
}
</script>

<style scoped>
.c-gray {
  color: #6b7280;
}
</style>
